'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserProfile = {
  id: string;
  email: string;
  username: string;
  organization: string;
  role: 'admin' | 'player';
  lastLoginAt?: string;
  createdAt?: string;
};

type AuthContextType = {
  user: ReturnType<typeof createClient> extends { auth: { getUser: () => Promise<{ data: { user: infer U } }> } } ? U | null : null;
  session: unknown;
  loading: boolean;
  profile: UserProfile | null;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<unknown>;
  signIn: (email: string, password: string) => Promise<unknown>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<unknown>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<unknown>(null);
  const [session, setSession] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const supabase = createClient();

  async function fetchProfile(userId: string, updateLoginTime = false): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error || !data) return null;
      // Only update last_login_at on actual sign-in, not every page load
      if (updateLoginTime) {
        await supabase
          .from('user_profiles')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', userId);
      }
      return {
        id: data.id,
        email: data.email,
        username: data.username,
        organization: data.organization || '',
        role: data.role as 'admin' | 'player',
        lastLoginAt: data.last_login_at,
        createdAt: data.created_at,
      };
    } catch {
      return null;
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id, false);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Only update last_login_at on actual sign-in events
        const isSignIn = _event === 'SIGNED_IN';
        const p = await fetchProfile(session.user.id, isSignIn);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: metadata?.username || metadata?.fullName || '',
          full_name: metadata?.fullName || metadata?.username || '',
          organization: metadata?.organization || '',
          role: 'player',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return (user as { email_confirmed_at?: string | null } | null)?.email_confirmed_at != null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    const u = user as { id?: string } | null;
    if (!u?.id) return null;
    return fetchProfile(u.id, false);
  };

  const refreshProfile = async () => {
    const u = user as { id?: string } | null;
    if (!u?.id) return;
    const p = await fetchProfile(u.id, false);
    setProfile(p);
  };

  const value: AuthContextType = {
    user: user as AuthContextType['user'],
    session,
    loading,
    profile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
